'use client';

import {useLocale} from 'next-intl';

/**
 * Documentation et Publications — rapports, publications et cadre juridique de l'UTSS.
 * Les documents sont déposés dans /public/docs/ ; tant que `url` est vide => « Bientôt disponible ».
 */
interface Doc {
  titre_fr: string;
  titre_ar: string;
  titre_en: string;
  groupe: 'rapports' | 'publications' | 'juridique';
  url: string; // ex: "/docs/rapport-annuel-2025.pdf" — vide = à venir
}

const DOCS: Doc[] = [
  {titre_fr: "Rapport d'activité annuel", titre_ar: 'التقرير السنوي للنشاط', titre_en: 'Annual activity report', groupe: 'rapports', url: ''},
  {titre_fr: 'Rapport financier', titre_ar: 'التقرير المالي', titre_en: 'Financial report', groupe: 'rapports', url: ''},
  {titre_fr: "Bilan d'impact social", titre_ar: 'حصيلة الأثر الاجتماعي', titre_en: 'Social impact report', groupe: 'rapports', url: ''},
  {titre_fr: "Brochure de présentation de l'UTSS", titre_ar: 'كتيب تقديم الاتحاد', titre_en: 'UTSS presentation brochure', groupe: 'publications', url: ''},
  {titre_fr: 'Guide des programmes sociaux', titre_ar: 'دليل البرامج الاجتماعية', titre_en: 'Social programs guide', groupe: 'publications', url: ''},
  {titre_fr: "Statuts de l'association", titre_ar: 'القانون الأساسي للجمعية', titre_en: 'Association statutes', groupe: 'juridique', url: ''},
  {titre_fr: 'Loi des associations N° 154 (1959)', titre_ar: 'قانون الجمعيات عدد 154 (1959)', titre_en: 'Associations Law No. 154 (1959)', groupe: 'juridique', url: ''},
];

const LABELS: Record<string, {
  home: string; crumb: string; title: string; intro: string; soon: string; download: string;
  groupes: Record<Doc['groupe'], string>;
}> = {
  fr: {
    home: 'Accueil', crumb: 'Documentation et Publications', title: 'Documentation et Publications',
    intro: "Retrouvez les rapports, publications et documents de référence de l'UTSS, dans un souci de transparence et de redevabilité.",
    soon: 'Bientôt disponible', download: 'Télécharger',
    groupes: {rapports: 'Rapports', publications: 'Publications', juridique: 'Cadre juridique'},
  },
  ar: {
    home: 'الرئيسية', crumb: 'الوثائق والمنشورات', title: 'الوثائق والمنشورات',
    intro: 'اطّلع على تقارير ومنشورات ووثائق الاتحاد المرجعية في إطار الشفافية والمساءلة.',
    soon: 'قريبا', download: 'تحميل',
    groupes: {rapports: 'التقارير', publications: 'المنشورات', juridique: 'الإطار القانوني'},
  },
  en: {
    home: 'Home', crumb: 'Documentation & Publications', title: 'Documentation & Publications',
    intro: 'Browse UTSS reports, publications and reference documents, in a spirit of transparency and accountability.',
    soon: 'Coming soon', download: 'Download',
    groupes: {rapports: 'Reports', publications: 'Publications', juridique: 'Legal framework'},
  },
};

const GROUPES: Doc['groupe'][] = ['rapports', 'publications', 'juridique'];

export default function DocumentationPage() {
  const locale = useLocale();
  const l = LABELS[locale] || LABELS.fr;
  const titreOf = (d: Doc) => (locale === 'ar' ? d.titre_ar : locale === 'en' ? d.titre_en : d.titre_fr);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="text-sm text-muted mb-6">
        <a href={`/${locale}`} className="hover:text-primary">{l.home}</a>
        <span className="mx-2">/</span>
        <span className="text-foreground">{l.crumb}</span>
      </nav>

      <h1 className="text-3xl font-bold text-primary mb-2">{l.title}</h1>
      <p className="text-muted mb-8 max-w-3xl">{l.intro}</p>

      <div className="space-y-8">
        {GROUPES.map(g => {
          const docs = DOCS.filter(d => d.groupe === g);
          if (docs.length === 0) return null;
          return (
            <section key={g}>
              <h2 className="text-lg font-semibold text-secondary mb-3 border-b border-border pb-2">{l.groupes[g]}</h2>
              <ul className="space-y-2">
                {docs.map((d, i) => {
                  const ready = !!d.url;
                  return (
                    <li key={i}>
                      <a
                        href={ready ? d.url : undefined}
                        target={ready ? '_blank' : undefined}
                        rel={ready ? 'noopener noreferrer' : undefined}
                        className={`flex items-center gap-3 p-3 rounded-lg border border-border transition-colors ${
                          ready ? 'hover:bg-surface cursor-pointer' : 'opacity-70 cursor-default'
                        }`}
                      >
                        <span className="w-9 h-9 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </span>
                        <span className="flex-1 text-sm font-medium text-foreground" dir="auto">{titreOf(d)}</span>
                        <span className={`text-xs shrink-0 ${ready ? 'text-primary font-medium' : 'text-muted'}`}>
                          {ready ? l.download : l.soon}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
